import React, { useState, useEffect } from 'react';
import useCrmStore from '../../../services/useCrmStore';
import { toast } from 'react-toastify';
import { api } from '../../../services/api';
import FullScreenLoader from '../../FullScreenLoader/FullScreenLoader';
import '../CrmModals.css';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiDollarSign,
  FiCalendar,
  FiTarget,
  FiAlertCircle,
  FiActivity,
  FiFileText,
  FiTag,
  FiX,
  FiPlus,
  FiSave,
  FiMessageSquare,
  FiSend,
  FiClock
} from 'react-icons/fi';


const EditLeadForm = ({ lead, stages, onClose, onSuccess }) => {
  const { updateLead } = useCrmStore();
  const [formData, setFormData] = useState({
    name: lead.name || '',
    email: lead.email || '',
    phone: lead.phone || '',
    company: lead.company || '',
    stage_id: lead.stage_id || '',
    source: lead.source || 'MANUAL',
    deal_value: lead.deal_value || '',
    expected_close_date: lead.expected_close_date || '',
    lead_score: lead.lead_score || 50,
    priority: lead.priority || 'MEDIUM',
    status: lead.status || 'ACTIVE',
    description: lead.description || '',
    tags: lead.tags || []
  });
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);


  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [sendingNote, setSendingNote] = useState(false);

  const fetchNotes = React.useCallback(async () => {
    setLoadingNotes(true);
    try {
      const response = await api.get(`/api/crm/leads/${lead.id}/activities`);
      const noteActivities = response.data.filter(a => a.type === 'NOTE_ADDED');
      setNotes(noteActivities);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  }, [lead.id]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setSendingNote(true);
    try {
      await api.post(`/api/crm/leads/${lead.id}/notes`, {
        content: newNote
      });
      setNewNote('');
      toast.success('Nota adicionada com sucesso');
      await fetchNotes();
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Falha ao adicionar nota');
    } finally {
      setSendingNote(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.stage_id) {
      newErrors.stage_id = 'Estágio é obrigatório';
    }

    if (formData.deal_value && isNaN(parseFloat(formData.deal_value))) {
      newErrors.deal_value = 'Valor inválido';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null,
        lead_score: parseInt(formData.lead_score),
        tags: formData.tags.length > 0 ? formData.tags : null
      };

      await updateLead(lead.id, payload);
      toast.success('Lead atualizado com sucesso!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error('Falha ao atualizar lead');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      LOW: '#22c55e',
      MEDIUM: '#f59e0b',
      HIGH: '#ef4444',
      URGENT: '#dc2626'
    };
    return colors[priority] || colors.MEDIUM;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sem data';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal crm-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-header">
          <div className="crm-modal-header-content">
            <h2 className="crm-modal-title">Editar Lead</h2>
            <p className="crm-modal-subtitle">
              Atualize as informações do lead {lead.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="crm-modal-close"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="crm-modal-body">
            <h3 className="crm-section-title">
              Informações Básicas
            </h3>

            <div className="crm-form-grid">
              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiUser className="crm-form-label-icon" />
                  Nome <span className="crm-form-label-required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`crm-input ${errors.name ? 'crm-input-error' : ''}`}
                  placeholder="Nome completo do lead"
                />
                {errors.name && (
                  <span className="crm-error-message">
                    <FiAlertCircle /> {errors.name}
                  </span>
                )}
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiMail className="crm-form-label-icon" />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`crm-input ${errors.email ? 'crm-input-error' : ''}`}
                  placeholder="email@example.com"
                />
                {errors.email && (
                  <span className="crm-error-message">
                    <FiAlertCircle /> {errors.email}
                  </span>
                )}
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiPhone className="crm-form-label-icon" />
                  Telefone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="crm-input"
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiBriefcase className="crm-form-label-icon" />
                  Empresa
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="crm-input"
                  placeholder="Nome da empresa"
                />
              </div>
            </div>
            <h3 className="crm-section-title crm-mt-8">
              Pipeline & Status
            </h3>

            <div className="crm-form-grid">
              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiActivity className="crm-form-label-icon" />
                  Estágio <span className="crm-form-label-required">*</span>
                </label>
                <select
                  name="stage_id"
                  value={formData.stage_id}
                  onChange={handleChange}
                  className={`crm-select ${errors.stage_id ? 'crm-input-error' : ''}`}
                >
                  <option value="">Selecione um estágio...</option>
                  {stages.map(stage => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
                {errors.stage_id && (
                  <span className="crm-error-message">
                    <FiAlertCircle /> {errors.stage_id}
                  </span>
                )}
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiTag className="crm-form-label-icon" />
                  Origem
                </label>
                <select
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  className="crm-select"
                >
                  <option value="MANUAL">Manual</option>
                  <option value="FORM">Formulário</option>
                  <option value="WHATSAPP">WhatsApp</option>
                  <option value="INSTAGRAM">Instagram</option>
                  <option value="WEBHOOK">Webhook</option>
                  <option value="API">API</option>
                </select>
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  Prioridade
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="crm-select"
                  style={{
                    borderColor: getPriorityColor(formData.priority)
                  }}
                >
                  <option value="LOW">Baixa</option>
                  <option value="MEDIUM">Média</option>
                  <option value="HIGH">Alta</option>
                  <option value="URGENT">Urgente</option>
                </select>
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="crm-select"
                >
                  <option value="ACTIVE">Ativo</option>
                  <option value="INACTIVE">Inativo</option>
                  <option value="BLOCKED">Bloqueado</option>
                  <option value="CONVERTED">Convertido</option>
                  <option value="LOST">Perdido</option>
                </select>
              </div>
            </div>

            <h3 className="crm-section-title crm-mt-8">
              Informações do Negócio
            </h3>

            <div className="crm-form-grid">
              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiDollarSign className="crm-form-label-icon" />
                  Valor do Negócio
                </label>
                <input
                  type="number"
                  name="deal_value"
                  value={formData.deal_value}
                  onChange={handleChange}
                  className={`crm-input ${errors.deal_value ? 'crm-input-error' : ''}`}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
                <span className="crm-form-hint">Valor estimado da oportunidade em R$</span>
                {errors.deal_value && (
                  <span className="crm-error-message">
                    <FiAlertCircle /> {errors.deal_value}
                  </span>
                )}
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiCalendar className="crm-form-label-icon" />
                  Data Prevista de Fechamento
                </label>
                <input
                  type="date"
                  name="expected_close_date"
                  value={formData.expected_close_date}
                  onChange={handleChange}
                  className="crm-input"
                />
                <span className="crm-form-hint">Quando espera fechar este negócio?</span>
              </div>

              <div className="crm-form-field crm-form-grid-full">
                <label className="crm-form-label">
                  <FiTarget className="crm-form-label-icon" />
                  Pontuação do Lead: {formData.lead_score}
                </label>
                <input
                  type="range"
                  name="lead_score"
                  value={formData.lead_score}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className="crm-input"
                />
                <span className="crm-form-hint">
                  Qualificação do lead de 0 a 100 (quanto maior, melhor qualificado)
                </span>
              </div>
            </div>

            <div className="crm-form-field crm-mt-4">
              <label className="crm-form-label">
                <FiFileText className="crm-form-label-icon" />
                Descrição
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="crm-textarea"
                placeholder="Adicione informações adicionais sobre este lead..."
                rows="4"
              />
              <span className="crm-form-hint">
                Detalhes, observações ou contexto adicional sobre o lead
              </span>
            </div>


            <div className="crm-form-field crm-mt-4">
              <label className="crm-form-label">
                <FiTag className="crm-form-label-icon" />
                Tags
              </label>
              <div className="crm-tag-input-wrapper">
                <div className="crm-tag-input-container">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={handleTagInputKeyPress}
                    className="crm-input crm-tag-input"
                    placeholder="Digite uma tag e pressione Enter..."
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="crm-btn-icon"
                    disabled={!tagInput.trim()}
                  >
                    <FiPlus />
                  </button>
                </div>

                {formData.tags.length > 0 && (
                  <div className="crm-tag-list">
                    {formData.tags.map(tag => (
                      <div key={tag} className="crm-tag">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="crm-tag-remove"
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span className="crm-form-hint">
                Use tags para categorizar e filtrar leads facilmente
              </span>
            </div>

            <h3 className="crm-section-title crm-mt-8">
              <FiMessageSquare />
              Notas e Observações
            </h3>

            <div className="crm-note-input-wrapper" style={{ flexDirection: 'column' }}>
              <div className="crm-flex-row crm-mb-4" style={{ width: '100%' }}>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Escreva uma nota sobre este lead..."
                  className="crm-textarea"
                  style={{ flex: 1, minHeight: '80px' }}
                  disabled={sendingNote}
                />
                <button
                  type="button"
                  onClick={handleAddNote}
                  className="crm-btn crm-btn-primary"
                  disabled={!newNote.trim() || sendingNote}
                >
                  {sendingNote ? (
                    <span className="crm-spinner" />
                  ) : (
                    <FiSend />
                  )}
                </button>
              </div>

              {loadingNotes ? (
                <div className="crm-loading-state">
                  <span className="crm-spinner" />
                  < FullScreenLoader message="Carregando notas..." />
                </div>
              ) : notes.length === 0 ? (
                <div className="crm-empty-state">
                  <FiAlertCircle className="crm-empty-state-icon" size={24} />
                  <p>Nenhuma nota adicionada ainda</p>
                </div>
              ) : (
                <div className="crm-notes-list">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="crm-note-item"
                    >
                      <div className="crm-note-meta">
                        <FiClock size={12} />
                        <span>{formatDate(note.created_at)}</span>
                      </div>
                      <p className="crm-note-content">
                        {note.activity_metadata?.preview || note.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="crm-modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="crm-btn crm-btn-secondary"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="crm-btn crm-btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="crm-spinner" />
                  Salvando...
                </>
              ) : (
                <>
                  <FiSave />
                  Salvar Alterações
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLeadForm;
